import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SpaceList } from './SpaceList';
import { SpaceNew } from './SpaceNew';
import { SpaceMigrate } from './SpaceMigrate';
import { SpaceEdit } from './SpaceEdit';
import { SpaceUsersNew } from './users/SpaceUsersNew';
import { SpaceUsersEdit } from './users/SpaceUsersEdit';
import { regHandlers } from '../../reducer';
import { Map } from 'immutable';
import { dispatch, regSaga } from '../../store';
import { navigate } from '../../root';
import { call, select, takeEvery } from 'redux-saga/effects';
import { deleteTenant } from '@kineticdata/react/src/apis/system';
import { useSelector } from 'react-redux';

regSaga(
  takeEvery('DELETE_SPACE', function* ({ payload }) {
    const deleteDatabase = yield select(state =>
      state.getIn(['tenant', 'dbDelete', 'willDeleteDb']),
    );

    const { error } = yield call(deleteTenant, {
      slug: payload,
      deleteDatabase,
    });
    if (!error) {
      navigate('/spaces', { replace: true });
    }
  }),
);

regHandlers({
  SET_DELETE_TENANT_DB: (state, { payload }) =>
    state.updateIn(['tenant', 'dbDelete'], Map(), task =>
      task
        .set('canDeleteTaskDb', payload.canDeleteTaskDb)
        .set('canDeleteIntegratorDb', payload.canDeleteIntegratorDb)
        .set('willDeleteDb', payload.willDeleteDb),
    ),
  SET_WILL_DELETE_TENANT_DB: (state, { payload }) =>
    state.setIn(
      ['tenant', 'dbDelete', 'willDeleteDb'],
      typeof payload === 'boolean'
        ? payload
        : !state.getIn(['tenant', 'dbDelete', 'willDeleteDb'], false),
    ),
  RESET_DELETE_TENANT_DB: state => state.deleteIn(['tenant', 'dbDelete']),
  SET_TENANT_FEATURES: (state, { payload }) =>
    state.setIn(['tenant', 'features'], payload),
  RESET_TENANT_FEATURES: state => state.deleteIn(['tenant', 'features']),
});

export const toggleWillDeleteDb = status =>
  dispatch('SET_WILL_DELETE_TENANT_DB', status);

const SpaceDeleteConfirmationBody = () => {
  const { canDeleteTaskDb, canDeleteIntegratorDb, willDeleteDb } = useSelector(
    state => ({
      canDeleteTaskDb: state.getIn(
        ['tenant', 'dbDelete', 'canDeleteTaskDb'],
        false,
      ),
      canDeleteIntegratorDb: state.getIn(
        ['tenant', 'dbDelete', 'canDeleteIntegratorDb'],
        false,
      ),
      willDeleteDb: state.getIn(['tenant', 'dbDelete', 'willDeleteDb'], false),
    }),
  );

  const names = [
    canDeleteTaskDb && 'Task',
    canDeleteIntegratorDb && 'Integrator',
  ].filter(Boolean);
  const plural = names.length !== 1;
  const inverseNames = [
    !canDeleteTaskDb && 'Task',
    !canDeleteIntegratorDb && 'Integrator',
  ].filter(Boolean);
  const inversePlural = inverseNames.length !== 1;

  return (
    <div>
      <p>
        Deleting a space is a permanent action. Are you sure you want to
        continue?
      </p>
      {names.length > 0 && (
        <>
          <div className="form-group">
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id="delete-task-db"
                checked={willDeleteDb}
                onChange={toggleWillDeleteDb}
              />
              <label className="form-check-label" htmlFor="delete-task-db">
                Delete {names.join(' and ')} DB
              </label>
            </div>
          </div>
          {!willDeleteDb && (
            <div className="alert alert-warning">
              You are opting out of deleting the SQL database{plural ? 's' : ''}{' '}
              used for {names.join(' and ')}. You will need to manually remove
              the database{plural ? 's' : ''} and user{plural ? 's' : ''}{' '}
              associated with this space before creating a new space with the
              same slug.
            </div>
          )}
        </>
      )}
      {names.length < 2 && (
        <div className="alert alert-warning">
          This process will not automatically delete the SQL database
          {inversePlural ? 's' : ''} used for {inverseNames.join(' and ')}. You
          will need to manually remove the database{inversePlural ? 's' : ''}{' '}
          and user{inversePlural ? 's' : ''} associated with this space before
          creating a new space with the same slug.
        </div>
      )}
    </div>
  );
};

export const spaceDeleteConfirmOptions = ({ spaceSlug }) => ({
  title: 'Are you sure you want to delete this Space?',
  body: SpaceDeleteConfirmationBody,
  actionName: 'Delete Space',
  actionType: 'danger',
  confirmationTextLabel: 'space slug',
  confirmationText: spaceSlug,
});

export const Spaces = () => (
  <Routes>
    <Route path="" element={<SpaceList />} />
    <Route path="new" element={<SpaceNew />} />
    <Route path="migrate/:spaceSlug" element={<SpaceMigrate />} />
    <Route path="edit/:spaceSlug" element={<Navigate to="general" replace />} />
    <Route path="edit/:spaceSlug/users/new" element={<SpaceUsersNew />} />
    <Route
      path="edit/:spaceSlug/users/edit/:username"
      element={<SpaceUsersEdit />}
    />
    <Route path="edit/:spaceSlug/:tab?/:subTab?" element={<SpaceEdit />} />
  </Routes>
);
